import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";

interface RequestDetails {
  reportId: string;
}

interface PBIEmbedState {
  isLoading: boolean;
  embedUrl: string | null;
  embedToken: string | null;
  error: string | null;
}

const initialState: PBIEmbedState = {
  isLoading: false,
  embedUrl: null,
  embedToken: null,
  error: null,
};

export const fetchPBIEmbedDetails = createAsyncThunk<
  { embedUrl: string; embedToken: string },
  RequestDetails
>(
  "CTPBIEmbedDetailsSlice/fetchPBIEmbedDetails",
  async ({ reportId }, thunkApi) => {
    try {
      const response = await httpClient.post(
        `/reporting/pbi/${encodeURIComponent(reportId)}`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          signal: thunkApi.signal,
        } as any
      );
      if (response.status !== 200) {
        return thunkApi.rejectWithValue({
          message: response.data.Message || "Failed to load embed details",
        });
      }
      const embedDetails = response?.data?.Data ?? response?.data;

      return { embedUrl: embedDetails.embedUrl, embedToken: embedDetails.embedToken };
    } catch (err: any) {
      if (thunkApi.signal.aborted) {
        return thunkApi.rejectWithValue({ message: "Request cancelled" });
      }
      return thunkApi.rejectWithValue({
        message: err?.message || "Failed to load embed details",
      });
    }
  }
);

const CTPBIEmbedDetailsSlice = createSlice({
  name: "CTPBIEmbedDetailsSlice",
  initialState,
  reducers: {
    resetPBIEmbedState: (state) => {
      state.isLoading = false;
      state.embedUrl = null;
      state.embedToken = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPBIEmbedDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.embedUrl = null;
        state.embedToken = null;
      })
      .addCase(
        fetchPBIEmbedDetails.fulfilled,
        (state, action: PayloadAction<{ embedUrl: string; embedToken: string }>) => {
          state.isLoading = false;
          state.embedUrl = action.payload.embedUrl;
          state.embedToken = action.payload.embedToken;
          state.error = null;
        }
      )
      .addCase(fetchPBIEmbedDetails.rejected, (state, action: any) => {
        state.isLoading = false;
        state.embedUrl = null;
        state.embedToken = null;
        state.error = action.payload?.message ?? "Failed to load embed details";
      });
  },
});

export const { resetPBIEmbedState } = CTPBIEmbedDetailsSlice.actions;
export default CTPBIEmbedDetailsSlice.reducer;